import { ArrowRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_maine-web/artifacts/qoipmzpw_WhatsApp%20Image%202023-11-30%20at%2017.56.14_ac124f9d.jpg";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain-overlay bg-[#0E1116]"
    >
      {/* Ambient glows */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-red/[0.08] blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-blueAccent/[0.06] blur-[140px] pointer-events-none" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#F5F1EA 1px, transparent 1px), linear-gradient(90deg, #F5F1EA 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-32 pb-16 min-h-screen flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: Title block */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <div data-testid="hero-eyebrow" className="flex items-center justify-center lg:justify-start gap-3 mb-8">
              <span className="h-px w-10 bg-brand-red" />
              <span className="text-[11px] uppercase tracking-[0.4em] text-brand-red font-medium font-sans">
                Manufacturing Excellence Since 2014
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display tracking-tight text-[#F5F1EA] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] font-medium"
            >
              Niryasa <br />
              <span className="italic text-brand-red font-normal">Industries</span>
              <span className="block text-base sm:text-lg lg:text-xl tracking-[0.4em] uppercase text-[#A8A29A] mt-5 font-sans font-medium">
                Pvt Ltd
              </span>
            </h1>

            <p
              data-testid="hero-description"
              className="mt-10 max-w-xl mx-auto lg:mx-0 text-sm md:text-base text-[#A8A29A] leading-relaxed font-sans tracking-wide"
            >
              Crafting premium aluminium &amp; copper welding cables and pressure-tested industrial hoses — engineered with metallurgical precision for India&apos;s heaviest industries.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-brand-red text-white px-9 py-4 uppercase tracking-[0.22em] text-[11px] font-semibold hover:bg-brand-redHover transition-colors font-sans"
              >
                Request a Quote
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#products"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-3 border border-[#F5F1EA]/20 text-[#F5F1EA] px-9 py-4 uppercase tracking-[0.22em] text-[11px] font-semibold hover:border-brand-red/60 hover:text-brand-red transition-all font-sans"
              >
                Explore Products
              </a>
            </div>
          </div>

          {/* Right: Creative Logo Composition */}
          <div className="lg:col-span-5 flex items-center justify-center">
            <div
              data-testid="hero-logo-composition"
              className="relative w-[320px] h-[320px] sm:w-[400px] sm:h-[400px] md:w-[480px] md:h-[480px]"
            >
              {/* Corner brackets — industrial blueprint */}
              <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-brand-red/70" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-brand-red/70" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-brand-red/70" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-brand-red/70" />

              {/* Crosshair guides */}
              <span className="absolute top-1/2 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#F5F1EA]/10 to-transparent" />
              <span className="absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-[#F5F1EA]/10 to-transparent" />

              {/* Outermost text ring — clockwise slow */}
              <svg
                className="absolute inset-0 w-full h-full logo-orbit-cw"
                viewBox="0 0 400 400"
                aria-hidden="true"
              >
                <defs>
                  <path
                    id="ringPathOuter"
                    d="M 200, 200 m -180, 0 a 180,180 0 1,1 360,0 a 180,180 0 1,1 -360,0"
                  />
                </defs>
                <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(184,115,51,0.22)" strokeWidth="1" />
                <text fill="#B87333" fontSize="10.5" letterSpacing="7" fontFamily="Manrope, sans-serif" fontWeight="600">
                  <textPath href="#ringPathOuter" startOffset="0">
                    NIRYASA INDUSTRIES PVT LTD  ✦  WELDING CABLES  ✦  WELDING HOSES  ✦  MADE IN INDIA  ✦  
                  </textPath>
                </text>
              </svg>

              {/* Second ring — counter-clockwise dashed */}
              <div className="absolute inset-[8%] rounded-full border border-dashed border-[#F5F1EA]/12 logo-orbit-ccw">
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-brand-red rounded-full shadow-[0_0_12px_2px_rgba(184,115,51,0.7)]" />
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-red rounded-full shadow-[0_0_12px_2px_rgba(184,115,51,0.7)]" />
                <span className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[#F5F1EA]/60 rounded-full" />
                <span className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[#F5F1EA]/60 rounded-full" />
              </div>

              {/* Third ring — orbiting labels */}
              <div className="absolute inset-[16%] logo-orbit-cw-fast">
                {[
                  { label: "Cables", pos: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" },
                  { label: "Hoses", pos: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2" },
                  { label: "2014", pos: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2" },
                  { label: "BIS", pos: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2" },
                ].map((chip, i) => (
                  <div
                    key={i}
                    className={`absolute ${chip.pos} bg-[#161A21] border border-brand-red/40 px-3 py-1 logo-orbit-counter`}
                    style={{ animationDuration: "60s" }}
                  >
                    <span className="text-[9px] uppercase tracking-[0.25em] text-brand-red font-sans font-semibold">
                      {chip.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Central glow */}
              <div className="absolute inset-[28%] rounded-full bg-brand-red/25 blur-3xl logo-pulse" />

              {/* Rotating conic accent behind logo */}
              <div className="absolute inset-[26%] rounded-full logo-orbit-cw-fast">
                <div
                  className="absolute inset-0 rounded-full opacity-40"
                  style={{
                    background:
                      "conic-gradient(from 0deg, transparent 0deg, #B87333 60deg, transparent 120deg, transparent 240deg, #457B9D 300deg, transparent 360deg)",
                    filter: "blur(8px)",
                  }}
                />
              </div>

              {/* Center logo — floating with subtle pulse (no full rotation, so it stays readable) */}
              <div className="absolute inset-[30%] flex items-center justify-center">
                <div className="relative w-full h-full logo-float">
                  <img
                    data-testid="hero-logo-image"
                    src={LOGO_URL}
                    alt="Niryasa Industries Logo"
                    className="w-full h-full object-cover rounded-full border border-brand-red/40 shadow-[0_0_40px_rgba(184,115,51,0.35)]"
                  />
                </div>
              </div>

              {/* Sparks — decorative floating dots */}
              <span className="absolute top-[12%] right-[18%] w-1 h-1 bg-brand-red rounded-full spark-1" />
              <span className="absolute top-[24%] left-[8%] w-1.5 h-1.5 bg-brand-red/80 rounded-full spark-2" />
              <span className="absolute bottom-[18%] right-[10%] w-1 h-1 bg-[#F5F1EA]/60 rounded-full spark-3" />
              <span className="absolute bottom-[26%] left-[20%] w-1 h-1 bg-brand-red rounded-full spark-4" />
              <span className="absolute top-[45%] right-[6%] w-0.5 h-0.5 bg-[#F5F1EA] rounded-full spark-2" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.4em] text-[#A8A29A]/60 font-sans">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-[#A8A29A]/40 to-transparent" />
      </div>
    </section>
  );
};
